import { Building2, Users, AlertTriangle, Plug } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { PlatformMetrics, TenantsByPlan } from "@/types/api";

interface PlatformMetricsCardsProps {
  metrics: PlatformMetrics | undefined;
  isLoading: boolean;
}

export function PlatformMetricsCards({ metrics, isLoading }: PlatformMetricsCardsProps) {
  const cards = [
    {
      title: "Active Tenants",
      value: metrics?.active_tenants ?? 0,
      subtitle: `${metrics?.total_tenants ?? 0} total`,
      icon: Building2,
    },
    {
      title: "Total Users",
      value: metrics?.total_users ?? 0,
      subtitle: "across all tenants",
      icon: Users,
    },
    {
      title: "Open Alerts",
      value: metrics?.open_alerts ?? 0,
      subtitle: `${metrics?.total_alerts ?? 0} total`,
      icon: AlertTriangle,
    },
    {
      title: "Integrations",
      value: metrics?.total_integrations ?? 0,
      subtitle: "configured",
      icon: Plug,
    },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => (
        <Card key={card.title}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {card.title}
            </CardTitle>
            <card.icon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-8 w-20 animate-pulse rounded bg-muted" />
            ) : (
              <>
                <div className="text-2xl font-bold">{card.value.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">{card.subtitle}</p>
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

interface TenantPlanChartProps {
  data: TenantsByPlan[] | undefined;
  isLoading: boolean;
}

export function TenantPlanBreakdown({ data, isLoading }: TenantPlanChartProps) {
  const planColors: Record<string, string> = {
    starter: "bg-blue-500",
    essential: "bg-cyan-500",
    growth: "bg-green-500",
    business: "bg-yellow-500",
    enterprise: "bg-purple-500",
    msp: "bg-red-500",
  };

  const total = data?.reduce((sum, d) => sum + d.count, 0) ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium">Tenants by Plan</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-24 animate-pulse rounded bg-muted" />
        ) : (
          <div className="space-y-3">
            {data?.map((item) => (
              <div key={item.plan} className="flex items-center gap-3">
                <div className={`h-3 w-3 rounded-full ${planColors[item.plan] ?? "bg-gray-500"}`} />
                <span className="w-24 text-sm capitalize">{item.plan}</span>
                <div className="flex-1">
                  <div className="h-2 rounded-full bg-muted">
                    <div
                      className={`h-2 rounded-full ${planColors[item.plan] ?? "bg-gray-500"}`}
                      style={{ width: total > 0 ? `${(item.count / total) * 100}%` : "0%" }}
                    />
                  </div>
                </div>
                <span className="w-8 text-right text-sm font-medium">{item.count}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
